import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/atoms/Button/Button';
import { Input } from '@/components/atoms/Input/Input';
import { academyTextbookApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';

type TextbookRow = {
  id: number;
  name?: string;
  type?: string;
  coverUrl?: string;
  registrant?: string;
  registrationDate?: string;
};

const TYPE_OPTIONS = [
  { value: '', label: '전체' },
  { value: '파닉스데이', label: '파닉스데이' },
  { value: '스타터_파닉스', label: '스타터 파닉스' },
  { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
  { value: '스타터_스트럭처', label: '스타터 스트럭처' },
  { value: '에세이', label: '에세이' },
  { value: '통번역', label: '통번역' },
  { value: '리딩북', label: '리딩북' },
];

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const AcademyTextbookList: React.FC = () => {
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix();
  const [loading, setLoading] = useState(false);
  const [textbooks, setTextbooks] = useState<TextbookRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [searchType, setSearchType] = useState('');

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await academyTextbookApi.list({ q: searchName, category: searchType, page: page - 1, size: pageSize });
      const data = res?.data || res;
      const content = data?.content || data?.data?.content || [];
      const totalElements = data?.totalElements || data?.data?.totalElements || 0;
      setTextbooks(content);
      setTotal(totalElements);
    } catch (e) {
      console.error('교재 목록 로드 실패', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchName, searchType]);

  const rows = useMemo(() => textbooks, [textbooks]);

  const goDetail = (id: number) => {
    const base = slugPrefix || '';
    navigate(`${base}/academy/textbook/${id}`);
  };

  return (
    <main className='flex flex-col flex-1 py-8 px-4 md:px-8'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='heading1'>교재 관리</h1>
          <p className='text-gray-500 text-sm'>교재를 선택해 유닛 내용을 미리 확인하세요.</p>
        </div>
      </div>

      <section className='mb-6 grid grid-cols-1 md:grid-cols-3 gap-3'>
        <Input
          placeholder='교재명 검색'
          value={searchName}
          onChange={(e) => {
            setPage(1);
            setSearchName((e.target as HTMLInputElement).value);
          }}
        />
        <select
          className='w-full border border-gray-200 rounded-lg px-3 py-2 text-sm'
          value={searchType}
          onChange={(e) => {
            setPage(1);
            setSearchType(e.target.value);
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className='flex items-center text-sm text-gray-500'>
          총 {total}개
        </div>
      </section>

      <div className='overflow-auto border border-gray-100 rounded-xl bg-white'>
        <table className='min-w-full text-sm'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-4 py-3 text-left font-semibold text-gray-700'>교재명</th>
              <th className='px-4 py-3 text-left font-semibold text-gray-700'>유형</th>
              <th className='px-4 py-3 text-left font-semibold text-gray-700'>등록일</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className='px-4 py-6 text-center text-gray-500'>
                  {loading ? '불러오는 중...' : '등록된 교재가 없습니다.'}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                className='hover:bg-gray-50 cursor-pointer border-t border-gray-100'
                onClick={() => goDetail(row.id)}
              >
                <td className='px-4 py-3 font-medium text-gray-900 flex items-center gap-3'>
                  {row.coverUrl ? (
                    <img src={row.coverUrl} alt={row.name} className='w-10 h-14 object-cover rounded-md border' />
                  ) : (
                    <div className='w-10 h-14 rounded-md bg-gray-100 border flex items-center justify-center text-xs text-gray-400'>No Image</div>
                  )}
                  <span>{row.name || '-'}</span>
                </td>
                <td className='px-4 py-3 text-gray-700'>{TYPE_OPTIONS.find((o) => o.value === row.type)?.label || row.type || '-'}</td>
                <td className='px-4 py-3 text-gray-500'>{formatDate(row.registrationDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='flex items-center justify-between mt-4'>
        <span className='text-sm text-gray-500'>페이지 {page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
        <div className='flex gap-2'>
          <Button
            intent='secondary'
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            이전
          </Button>
          <Button
            onClick={() => setPage((p) => (p * pageSize < total ? p + 1 : p))}
            disabled={page * pageSize >= total}
          >
            다음
          </Button>
        </div>
      </div>
    </main>
  );
};

export default AcademyTextbookList;
